import {
  Banner,
  Button,
  Buttons,
  Container,
  ControlGroup,
  Form,
  Header,
  IControlChangeEventArgs,
  Input,
  Layout,
  Level,
  LevelGroup,
  LevelItem,
  MainContent,
  Section,
  SectionHeader,
  Select,
  Text,
} from '@inmotionnow/momentum-components-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import { toast } from 'react-toastify'

import { useAuth } from '@/auth/AuthContext'
import {
  generateUserTypeOptions,
  userTypeIdToValue,
  userTypeValueToId,
  UserStatusId,
} from '@/features/users/user-utils'
import {
  type UserDetail,
  UserTypeId,
  useGetCurrentUserQuery,
  useUpdateUserMutation,
  useUserDetails,
} from '@/features/users/userApi'
import {
  isEmailConflictError,
  isForbiddenError,
  isIdentityManagedBySsoError,
  isInsufficientManagePermissionError,
  isPermissionCheckUnavailableError,
  isSeatLimitError,
} from '@/features/users/userErrors'
import { isValidEmail } from '@/utils/validation'

export type IUserDetailsComponentProps = {}

interface UserProfile {
  first_name: string
  last_name: string
  title: string
  phone: string
  email: string
  user_type_id: UserTypeId | null
  is_active: boolean
}

const userProfileFormId = 'user-profile-form'

const profileFromRecord = (record: UserDetail): UserProfile => ({
  first_name: record.first_name,
  last_name: record.last_name,
  title: record.title || '',
  phone: record.phone || '',
  email: record.email,
  user_type_id: userTypeValueToId(record.user_type),
  is_active: record.is_active,
})

const UserDetailsView = (_props: IUserDetailsComponentProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  useLocation()
  const [searchParams] = useSearchParams()
  // The page edits the record it loaded, so the account is never in question.
  // ?accountId= survives only as the marker that the viewer arrived from an
  // account screen and Cancel should take them back there rather than just
  // reset the form; where "there" is comes from the record itself.
  const arrivedFromAccountScreen = searchParams.has('accountId')
  const userId =
    id && !isNaN(Number.parseInt(id, 10)) ? Number.parseInt(id, 10) : undefined

  const {
    data: userData,
    isLoading,
    isError,
    refetch: refetchUserDetails,
  } = useUserDetails(typeof userId === 'number' ? userId : undefined)
  const [updateUser, { isLoading: isSaving }] = useUpdateUserMutation()

  const userTypeOptions = React.useMemo(() => generateUserTypeOptions(t), [t])

  const { isStaff } = useAuth()
  const { data: currentUserData } = useGetCurrentUserQuery(undefined, {
    skip: isStaff,
  })

  // Scoped to the account the record belongs to, not to the viewer's own realm:
  // those can differ, and enabling the controls for an account the viewer
  // doesn't administer only invites a 403 from the backend, which authorizes the
  // write against this same account.
  const viewerIsAdmin = React.useMemo(() => {
    if (isStaff) return true
    if (!userData || !currentUserData) return false
    return (
      currentUserData.account.id === userData.account.id &&
      currentUserData.user_type === 'admin'
    )
  }, [isStaff, userData, currentUserData])

  // Keycloak owns email and both names for a federated user, so GIM shows them
  // but refuses to write them. The local flag locks the fields the moment the
  // backend refuses an identity edit, without waiting for the reloaded record.
  const [identityRefusedBySso, setIdentityRefusedBySso] = React.useState(false)
  // A refusal belongs to the record it was raised for; opening another user clears it.
  React.useEffect(() => {
    setIdentityRefusedBySso(false)
  }, [userId])
  const identityReadOnly =
    userData?.sso_linked_at != null || identityRefusedBySso
  const identityHelpText = identityReadOnly
    ? t('IDENTITY_MANAGED_BY_SSO')
    : undefined

  const [profile, setProfile] = React.useState<UserProfile>({
    first_name: '',
    last_name: '',
    title: '',
    phone: '',
    email: '',
    user_type_id: null,
    is_active: true,
  })
  const [validationErrors, setValidationErrors] = React.useState<
    Partial<Record<keyof UserProfile, string>>
  >({})

  // Config-driven validation rules
  const validationRules: {
    [K in keyof Omit<UserProfile, 'is_active'>]: Array<{
      validate: (value: string) => boolean
      message: string
    }>
  } = {
    first_name: [
      { validate: v => !!v.trim(), message: t('FIRST_NAME_REQUIRED') },
      {
        validate: v => v.trim().length <= 30,
        message: t('FIRST_NAME_MAX_LENGTH', { max: 30 }),
      },
    ],
    last_name: [
      { validate: v => !!v.trim(), message: t('LAST_NAME_REQUIRED') },
      {
        validate: v => v.trim().length <= 30,
        message: t('LAST_NAME_MAX_LENGTH', { max: 30 }),
      },
    ],
    title: [
      {
        validate: v => v.length <= 70,
        message: t('TITLE_MAX_LENGTH', { max: 70 }),
      },
    ],
    phone: [
      {
        validate: v => v.length <= 30,
        message: t('PHONE_MAX_LENGTH', { max: 30 }),
      },
    ],
    email: [
      { validate: v => !!v.trim(), message: t('EMAIL_REQUIRED') },
      { validate: v => isValidEmail(v.trim()), message: t('EMAIL_INVALID') },
    ],
    user_type_id: [
      {
        validate: v => v !== 'null' && v !== '-1',
        message: t('USER_TYPE_REQUIRED'),
      },
    ],
  }

  // A federated user's identity fields are read-only and may legitimately be
  // blank server-side, so validating them would block an otherwise valid save.
  const editableTextFields: (keyof Omit<
    UserProfile,
    'is_active' | 'user_type_id'
  >)[] = identityReadOnly
    ? ['title', 'phone']
    : ['first_name', 'last_name', 'title', 'phone', 'email']

  const validateForm = (
    profile: UserProfile,
  ): Partial<Record<keyof UserProfile, string>> => {
    const errors: Partial<Record<keyof UserProfile, string>> = {}
    const userProfileFields: (keyof Omit<UserProfile, 'is_active'>)[] = [
      ...editableTextFields,
      'user_type_id',
    ]
    userProfileFields.forEach(field => {
      for (const rule of validationRules[field]) {
        if (!rule.validate(String(profile[field] ?? ''))) {
          errors[field] = rule.message
          break
        }
      }
    })
    return errors
  }

  React.useEffect(() => {
    if (userData) {
      setProfile(profileFromRecord(userData))
    }
  }, [userData])

  const handleFieldChange = React.useCallback(
    (field: keyof UserProfile) => (e: IControlChangeEventArgs) => {
      setProfile(prev => ({ ...prev, [field]: e.currentValue }))

      // Remove error for this field if it exists
      if (validationErrors[field]) {
        setValidationErrors(prev => {
          const { [field]: _, ...rest } = prev
          return rest
        })
      }
    },
    [validationErrors],
  )

  const handleSubmit = (event?: React.FormEvent<Element>) => {
    if (event) {
      event.preventDefault()
    }

    if (!userId) return

    const errors = validateForm(profile)
    setValidationErrors(errors)
    if (Object.keys(errors).length > 0) {
      return
    }

    const userTypeValue = userTypeIdToValue(profile.user_type_id)

    const conditionedProfile = {
      first_name: profile.first_name?.trim() || '',
      last_name: profile.last_name?.trim() || '',
      title: profile.title?.trim() || '',
      phone: profile.phone?.trim() || '',
      email: profile.email?.trim() || '',
    }

    const hasTextChanges = editableTextFields.some(
      field => (conditionedProfile[field] || '') !== (userData?.[field] || ''),
    )
    const hasTypeChange =
      !!userTypeValue && userTypeValue !== userData?.user_type

    const hasIsActiveChange =
      profile.is_active !== (userData?.is_active ?? true)

    if (!hasTextChanges && !hasTypeChange && !hasIsActiveChange) {
      toast.info(t('NO_CHANGES_TO_SAVE'))
      return
    }

    updateUser({
      id: userId,
      data: {
        title: conditionedProfile.title,
        phone: conditionedProfile.phone,
        ...(identityReadOnly
          ? {}
          : {
              first_name: conditionedProfile.first_name,
              last_name: conditionedProfile.last_name,
              email: conditionedProfile.email,
            }),
        ...(hasTypeChange && userTypeValue ? { user_type: userTypeValue } : {}),
        ...(hasIsActiveChange ? { is_active: profile.is_active } : {}),
      },
    })
      .unwrap()
      .then(() => {
        toast.success(t('USER_UPDATED_SUCCESSFULLY'))
      })
      .catch((error: unknown) => {
        if (isEmailConflictError(error)) {
          setValidationErrors(prev => ({
            ...prev,
            email: t('EMAIL_ALREADY_IN_USE'),
          }))
        } else {
          // Specific rejections get their own copy; anything else falls through to
          // the generic message, as before. The backend runs the account-user path —
          // which carries these checks — before it writes anything, so on every one
          // of these the save was refused in full and restoring the form below shows
          // the true server state (KPS-444).
          // isInsufficientManagePermissionError: a 403 carrying
          // insufficient_manage_permission — the signed-in user may not assign the
          // requested user type. Its copy is update-specific: this screen re-types an
          // existing user, it never adds one.
          // isForbiddenError: any other 403, checked after the coded one above.
          // isPermissionCheckUnavailableError: a 503 — the backend could not get a
          // trustworthy answer from Workflow and refused rather than skip the check.
          // isSeatLimitError: a 409 carrying seat_limit_exceeded — the change would
          // exceed the account's Workflow Team Member limit, by activating a disabled
          // user or promoting one who holds a free Workflow Stakeholder seat.
          // isIdentityManagedBySsoError: a 400 carrying identity_managed_by_sso
          // — the user became federated while the page was open, so the identity
          // fields were still editable when the change was made. Reloading the
          // record brings in its federated state, which locks those fields.
          let message = t('ERROR_UPDATING_USER')
          if (isIdentityManagedBySsoError(error)) {
            message = t('IDENTITY_MANAGED_BY_SSO')
            setIdentityRefusedBySso(true)
            void refetchUserDetails()
          } else if (isInsufficientManagePermissionError(error)) {
            message = t('INSUFFICIENT_MANAGE_PERMISSION_ON_UPDATE')
          } else if (isForbiddenError(error)) {
            message = t('PERMISSION_DENIED_CREATING_USER')
          } else if (isPermissionCheckUnavailableError(error)) {
            message = t('PERMISSION_CHECK_UNAVAILABLE')
          } else if (isSeatLimitError(error)) {
            message = t('NO_TEAM_MEMBER_SEATS_AVAILABLE')
          }
          toast.error(message)
          if (userData) {
            setProfile(profileFromRecord(userData))
          }
        }
      })
  }

  const handleCancel = () => {
    if (!userData) return
    if (arrivedFromAccountScreen) {
      // Back to the account this record belongs to — never to the account named
      // in the URL, which may be a stale bookmark for an account the user has
      // since left.
      const returnAccountId = userData.account.id
      navigate(
        isStaff
          ? `/admin/account/details/${returnAccountId}`
          : `/account/${returnAccountId}/users`,
      )
      return
    }
    setProfile(profileFromRecord(userData))
    setValidationErrors({})
  }

  // If no ID is provided or user not found, show error message
  if (isLoading || isError || !userData || !userId) {
    const message = isLoading ? t('LOADING') : t('USER_NOT_FOUND_MESSAGE')
    return (
      <Layout>
        <Header>
          <Banner title={t('USER_DETAILS')} />
        </Header>
        <MainContent>
          <Section>
            <Container size='med'>
              <Text>{message}</Text>
            </Container>
          </Section>
        </MainContent>
      </Layout>
    )
  }

  return (
    <Layout>
      <Header>
        <Banner title={userData.display_name} />
      </Header>

      <MainContent>
        <Section padding={['xxxl', 'xl']}>
          <Container size='med'>
            <SectionHeader title={t('PROFILE')} colorize={true} />
            <Section padding={['xl', 'none', 'none', 'xxl']}>
              <Form id={userProfileFormId} onSubmit={handleSubmit}>
                <Form.Grid>
                  <ControlGroup
                    control={
                      <Input
                        disabled={isSaving || identityReadOnly}
                        handleChange={handleFieldChange('first_name')}
                        hasError={!!validationErrors.first_name}
                        id='first-name'
                        required={true}
                        value={profile.first_name || ''}
                        maxLength={30}
                        dataTestingId='user-first-name-input'
                        dataTrackingId='user-first-name-input'
                      />
                    }
                    label={t('FIRST_NAME')}
                    htmlFor='first-name'
                    helpText={validationErrors.first_name ?? identityHelpText}
                  />
                  <ControlGroup
                    control={
                      <Input
                        disabled={isSaving || identityReadOnly}
                        handleChange={handleFieldChange('last_name')}
                        hasError={!!validationErrors.last_name}
                        id='last-name'
                        required={true}
                        value={profile.last_name || ''}
                        maxLength={30}
                        dataTestingId='user-last-name-input'
                        dataTrackingId='user-last-name-input'
                      />
                    }
                    label={t('LAST_NAME')}
                    htmlFor='last-name'
                    helpText={validationErrors.last_name ?? identityHelpText}
                  />
                  <ControlGroup
                    control={
                      <Input
                        disabled={isSaving}
                        handleChange={handleFieldChange('title')}
                        hasError={!!validationErrors.title}
                        id='title'
                        required={false}
                        value={profile.title || ''}
                        maxLength={70}
                        dataTestingId='user-title-input'
                        dataTrackingId='user-title-input'
                      />
                    }
                    label={t('TITLE')}
                    htmlFor='title'
                    helpText={validationErrors.title}
                  />
                  <ControlGroup
                    control={
                      <Input
                        disabled={isSaving}
                        handleChange={handleFieldChange('phone')}
                        hasError={!!validationErrors.phone}
                        id='phone'
                        required={false}
                        type='tel'
                        value={profile.phone || ''}
                        maxLength={30}
                        dataTestingId='user-phone-input'
                        dataTrackingId='user-phone-input'
                      />
                    }
                    label={t('PHONE_NUMBER')}
                    htmlFor='phone'
                    helpText={validationErrors.phone}
                  />
                  <ControlGroup
                    control={
                      <Input
                        disabled={isSaving || identityReadOnly}
                        handleChange={handleFieldChange('email')}
                        hasError={!!validationErrors.email}
                        autoCapitalize='none'
                        id='email'
                        required={true}
                        type='email'
                        value={profile.email || ''}
                        maxLength={254}
                        dataTestingId='user-email-input'
                        dataTrackingId='user-email-input'
                      />
                    }
                    label={t('EMAIL_ADDRESS')}
                    htmlFor='email'
                    helpText={validationErrors.email ?? identityHelpText}
                  />
                  <ControlGroup
                    control={
                      <Select
                        disabled={isSaving || !viewerIsAdmin}
                        id='platform-user-type'
                        selectedValue={profile.user_type_id ?? -1}
                        selectOptions={userTypeOptions}
                        handleChange={(e: IControlChangeEventArgs) => {
                          const value = Number(e.currentValue)
                          setProfile(prev => ({
                            ...prev,
                            user_type_id:
                              value === UserTypeId.Admin ||
                              value === UserTypeId.TeamMember
                                ? value
                                : null,
                          }))
                          // Remove error for this field if it exists
                          if (validationErrors.user_type_id) {
                            setValidationErrors(prev => {
                              const { user_type_id: _, ...rest } = prev
                              return rest
                            })
                          }
                        }}
                        required={true}
                        hasError={!!validationErrors.user_type_id}
                        dataTestingId='user-platform-user-type-select'
                        dataTrackingId='user-platform-user-type-select'
                      />
                    }
                    label={t('USER_TYPE')}
                    htmlFor='platform-user-type'
                    helpText={validationErrors.user_type_id}
                  />
                  <ControlGroup
                    control={
                      <Select
                        disabled={isSaving || !viewerIsAdmin}
                        id='user-status'
                        selectedValue={
                          profile.is_active
                            ? UserStatusId.Active
                            : UserStatusId.Disabled
                        }
                        selectOptions={[
                          {
                            value: UserStatusId.Active,
                            displayText: t('ACTIVE'),
                          },
                          {
                            value: UserStatusId.Disabled,
                            displayText: t('DISABLED'),
                          },
                        ]}
                        handleChange={(e: IControlChangeEventArgs) => {
                          setProfile(prev => ({
                            ...prev,
                            is_active:
                              Number(e.currentValue) === UserStatusId.Active,
                          }))
                        }}
                        required={true}
                        dataTestingId='user-status-select'
                        dataTrackingId='user-status-select'
                      />
                    }
                    label={t('USER_STATUS')}
                    htmlFor='user-status'
                  />
                </Form.Grid>
                <Level>
                  <LevelGroup position='left'>
                    <LevelItem>
                      <Buttons>
                        <Form.Submit
                          formId={userProfileFormId}
                          label={t(isSaving ? 'SAVING' : 'SAVE')}
                          loading={isSaving}
                          dataTestingId='user-save-button'
                        />
                        <Button
                          disabled={isSaving}
                          onClick={handleCancel}
                          label={t('CANCEL')}
                          theme='flat'
                          dataTestingId='user-cancel-button'
                        />
                      </Buttons>
                    </LevelItem>
                  </LevelGroup>
                </Level>
              </Form>
            </Section>
          </Container>
        </Section>
      </MainContent>
    </Layout>
  )
}

export { UserDetailsView }
